import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

const baseUrl = `${process.env.REACT_APP_ONBOARDING_SERVICE_API}`

const PORTAL_ACCESS_TOKEN = 'portalAccessToken'
const PORTAL_LONG_TOKEN = 'portalLongToken'

export const portalStorage = {
  getAccessToken: () => localStorage.getItem(PORTAL_ACCESS_TOKEN),
  setAccessToken: (token: string) => localStorage.setItem(PORTAL_ACCESS_TOKEN, token),
  getLongToken: () => localStorage.getItem(PORTAL_LONG_TOKEN),
  setLongToken: (token: string) => localStorage.setItem(PORTAL_LONG_TOKEN, token),
  clear: () => {
    localStorage.removeItem(PORTAL_ACCESS_TOKEN)
    localStorage.removeItem(PORTAL_LONG_TOKEN)
  },
}

interface ExchangeResponse {
  accessToken: string
  expiresInSeconds: number
  profile: PortalProfile
}

export interface PortalProfile {
  customerCompanyName?: string
  customerEmail?: string
  truckingCompanyName?: string
}

export interface PortalOrderRow {
  orderId: number
  orderNumber: string
  loadNumber: string
  waybillNumber?: string
  shipperName?: string
  receiverName?: string
  lastStatus?: string
  lastStatusAt?: string
}

export interface PortalOrderPage {
  rows: PortalOrderRow[]
  size: number
  totalElements: number
  totalPages: number
  number: number
}

export interface PortalDocumentRef {
  documentName: string
  originalName: string
  s3Path: string
}

// Customer-side documents view — LC, Waybill, BOL, POD returned by the backend.
export interface PortalDocumentsResponse {
  LC?: PortalDocumentRef[]
  WAYBILL?: PortalDocumentRef[]
  BOL?: PortalDocumentRef[]
  POD?: PortalDocumentRef[]
}

export interface PortalPresignedUrlResponse {
  preSignedURL: string
}

const portalBaseQuery = fetchBaseQuery({
  baseUrl: baseUrl!,
  timeout: 60_000,
  prepareHeaders: (headers) => {
    const t = portalStorage.getAccessToken()
    if (t) headers.set('Authorization', `Bearer ${t}`)
    return headers
  },
})

// Wraps the base query so that on 401 we silently re-exchange the long token
// for a fresh access token and retry once. Failures cascade to a 401 the
// caller can act on (show "link expired").
const portalBaseQueryWithReauth: typeof portalBaseQuery = async (
  args,
  api,
  extraOptions
) => {
  let result = await portalBaseQuery(args, api, extraOptions)
  if (result.error && (result.error.status === 401 || result.error.status === 403)) {
    const longToken = portalStorage.getLongToken()
    if (!longToken) return result
    const refresh = await portalBaseQuery(
      {
        url: 'oapi/v1/customer/auth/exchange',
        method: 'POST',
        body: { token: longToken },
      },
      api,
      extraOptions
    )
    if (refresh.data) {
      const data = refresh.data as ExchangeResponse
      portalStorage.setAccessToken(data.accessToken)
      result = await portalBaseQuery(args, api, extraOptions)
    } else {
      portalStorage.clear()
    }
  }
  return result
}

export const customerPortalRuntimeApi = createApi({
  reducerPath: 'customerPortalRuntimeApi',
  baseQuery: portalBaseQueryWithReauth,
  endpoints: (builder) => ({
    exchangeToken: builder.mutation<ExchangeResponse, { token: string }>({
      query: (body) => ({
        url: 'oapi/v1/customer/auth/exchange',
        method: 'POST',
        body,
      }),
    }),
    getPortalMe: builder.query<PortalProfile, void>({
      query: () => ({ url: 'oapi/v1/customer/portal/me' }),
    }),
    getPortalOrders: builder.query<
      PortalOrderPage,
      { status?: string; page?: number; size?: number; search?: string }
    >({
      query: ({ status, page = 0, size = 25, search }) => ({
        url: 'oapi/v1/customer/portal/orders',
        params: {
          status,
          page,
          size,
          ...(search?.trim() ? { search: search.trim() } : {}),
        },
      }),
    }),
    getPortalOrderDocuments: builder.query<
      PortalDocumentsResponse,
      { orderId: number }
    >({
      query: ({ orderId }) => ({
        url: `oapi/v1/customer/portal/orders/${orderId}/documents`,
      }),
    }),
    getPortalDocumentPresignedUrl: builder.mutation<
      PortalPresignedUrlResponse,
      { s3URL: string; openInline?: boolean }
    >({
      // Calls the same task-automation endpoint the operator UI uses.
      // The gateway routes by the JWT's `domain` claim — a customer JWT
      // (domain=CUSTOMER_PORTAL) lands at the customer authorize URL.
      query: ({ s3URL, openInline }) => ({
        url: 'tapi/v1/docs/presigned-url',
        method: 'POST',
        body: { s3URL, openInline },
      }),
    }),
  }),
})

export const {
  useExchangeTokenMutation,
  useGetPortalMeQuery,
  useGetPortalOrdersQuery,
  useGetPortalOrderDocumentsQuery,
  useGetPortalDocumentPresignedUrlMutation,
} = customerPortalRuntimeApi
